import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { BalanceMatrixCell, BalanceMatrixRow, LeaveCreditsLeaveType } from '@lms/shared-types'
import { Modal } from '../../components/primitives/Modal'
import { ConfirmationDialog } from '../../components/primitives/ConfirmationDialog'
import { Avatar } from '../../components/primitives/Avatar'
import { Button } from '../../components/primitives/Button'
import { Icon } from '../../components/primitives/Icon'
import { ALL_LEAVE_TYPES, LEAVE_TYPE_STYLE } from '../../lib/leave-type-palette'
import { fmtDate } from '../../lib/format'
import { flags } from '../../lib/feature-flags'
import { applyAdjustment, deleteOverride } from '../../api/leave-credits'
import { ApiError } from '../../api/client'

/**
 * Per-employee balance detail — the "expand" pop-up for a Balances roster row.
 *
 * Read mode shows every leave type's entitled / used / pending / remaining +
 * expiry. Clicking "Adjust" flips it into an editable mode: each adjustable
 * type (applicable, entitlement-style — not replacement/unpaid) makes the
 * Entitled cell an input pre-filled with the current value, a shared reason is
 * collected, and Apply posts one adjustment per changed type (the absolute
 * target entitlement) via POST /leave-credits/adjustments. The displayed
 * numbers update from each response and the roster query is invalidated.
 */

const CURRENT_YEAR = new Date().getFullYear()

// Replacement is grant-managed and unpaid has no entitlement, so neither is a
// delta-adjustable balance. Everything else applicable can be adjusted.
const NOT_ADJUSTABLE = new Set<LeaveCreditsLeaveType>(['replacement', 'unpaid'])

// The credits matrix never carries the retired marriage_family_care type, so
// iterate the credit-eligible types (every LeaveTypeId except the retired one).
const CREDIT_LEAVE_TYPES = ALL_LEAVE_TYPES.filter(
  (lt): lt is LeaveCreditsLeaveType => lt !== 'marriage_family_care',
)

function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

/** Round to the nearest half-day — adjustments are half-day precision. */
function toHalf(n: number): number {
  return Math.round(n * 2) / 2
}

export interface BalancesDetailModalProps {
  row: BalanceMatrixRow | null
  onClose: () => void
  /** Open straight in edit mode (from a row's Adjust button). */
  startInEdit?: boolean
}

export function BalancesDetailModal({
  row,
  onClose,
  startInEdit = false,
}: BalancesDetailModalProps): JSX.Element | null {
  const queryClient = useQueryClient()
  const [cells, setCells] = useState<Record<string, BalanceMatrixCell>>({})
  const [editing, setEditing] = useState(false)
  const [deltas, setDeltas] = useState<Record<string, number>>({})
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)
  // The leave type currently being reset (override removed), or null. Disables
  // the row's controls and shows a spinner on that reset button only.
  const [resetting, setResetting] = useState<string | null>(null)
  // The pending reset awaiting confirmation, or null. Opens the confirm dialog.
  const [confirmReset, setConfirmReset] = useState<
    { lt: string; overrideId: string; label: string } | null
  >(null)
  const reasonRef = useRef<HTMLTextAreaElement>(null)

  // Reset all state only when the modal opens on a *different* employee — keying
  // on the id (not the whole `row`) so a background roster refetch, which passes
  // a new row object with the same id, doesn't wipe an in-progress edit.
  useEffect(() => {
    setCells(row ? { ...row.cells } : {})
    // Open in edit mode when launched from a row's Adjust button — but only if
    // the employee actually has an adjustable type.
    const anyAdjustable = row
      ? CREDIT_LEAVE_TYPES.some((lt) => row.cells[lt]?.eligible && !NOT_ADJUSTABLE.has(lt))
      : false
    setEditing(startInEdit && anyAdjustable)
    setDeltas({})
    setReason('')
    setError(null)
    // Clear the in-flight reset marker + any pending confirm: this modal
    // instance persists across close/reopen (it renders null when row is null),
    // so a reset that closed the modal would otherwise leave `resetting` set and
    // disable every input on the next open.
    setResetting(null)
    setConfirmReset(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.employeeId, startInEdit])

  if (!row) return null

  const isAdjustable = (lt: LeaveCreditsLeaveType): boolean =>
    Boolean(cells[lt]?.eligible) && !NOT_ADJUSTABLE.has(lt)
  const adjustableCount = CREDIT_LEAVE_TYPES.filter(isAdjustable).length
  const changed = Object.entries(deltas).filter(([, d]) => d !== 0)
  // Enabled as soon as there's a pending change — a missing reason is caught on
  // click (with an inline error) rather than leaving the button dead with no
  // hint as to why.
  const canApply = changed.length > 0 && !applying

  const setDelta = (lt: string, value: number): void => {
    setDeltas((prev) => ({ ...prev, [lt]: toHalf(value) }))
    setError(null)
  }

  const cancelEdit = (): void => {
    setEditing(false)
    setDeltas({})
    setReason('')
    setError(null)
  }

  async function handleApply(): Promise<void> {
    if (!row || changed.length === 0) return
    if (reason.trim().length === 0) {
      setError('A reason is required for adjustments.')
      // Bring the reason field into view — it can sit below the fold on a tall
      // roster, so a silent off-screen error reads as "nothing happened".
      reasonRef.current?.focus()
      return
    }
    setApplying(true)
    setError(null)
    try {
      const next = { ...cells }
      // Sequential so a mid-batch failure leaves the earlier ones applied and
      // surfaces which one broke (the server audits each individually).
      for (const [lt, delta] of changed) {
        // `delta` is the internal edit; the API takes the absolute target =
        // the type's current entitled + that edit (what HR typed).
        const target = (cells[lt]?.entitled ?? 0) + delta
        const resp = await applyAdjustment({
          employeeId: row.employeeId,
          leaveType: lt as LeaveCreditsLeaveType,
          entitlement: target,
          reason: reason.trim(),
        })
        const cur = next[lt]
        if (cur) {
          next[lt] = {
            ...cur,
            entitled: resp.row.entitled,
            used: resp.row.used,
            pending: resp.row.pending,
            remaining: resp.row.remaining,
            // An adjustment creates/updates the override — carry its id so the
            // reset affordance appears immediately (no roster refetch needed).
            overrideId: resp.row.overrideId ?? null,
          }
        }
      }
      setCells(next)
      setDeltas({})
      setReason('')
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: ['balances-matrix'] })
    } catch (err: unknown) {
      const status = err instanceof ApiError ? ` (${err.status})` : ''
      setError((err instanceof Error ? err.message : 'Adjustment failed') + status)
    } finally {
      setApplying(false)
    }
  }

  // Remove a type's override, rolling its entitlement back to the cohort default.
  // The server re-propagates the balance, so we refresh the roster and close —
  // reopening shows the rolled-back value. Closing avoids showing a stale
  // (still-override) entitlement in the still-open modal.
  async function handleReset(lt: string, overrideId: string): Promise<void> {
    if (!row) return
    setResetting(lt)
    setError(null)
    try {
      await deleteOverride(overrideId)
      void queryClient.invalidateQueries({ queryKey: ['balances-matrix'] })
      setConfirmReset(null)
      onClose()
    } catch (err: unknown) {
      const status = err instanceof ApiError ? ` (${err.status})` : ''
      setError((err instanceof Error ? err.message : 'Reset failed') + status)
      setResetting(null)
      setConfirmReset(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={row.employeeName}
      subtitle={`${row.jobTitle} · leave balances ${CURRENT_YEAR}`}
      // One fixed width for both modes — toggling Adjust reveals the inputs in
      // place rather than resizing the modal (which read as a new modal opening).
      maxWidth={760}
      footer={
        editing ? (
          <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="md" onClick={cancelEdit} disabled={applying}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              data-testid="balances-apply-adjust"
              onClick={() => void handleApply()}
              disabled={!canApply}
              loading={applying}
            >
              Apply adjustments
            </Button>
          </span>
        ) : adjustableCount > 0 ? (
          <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              size="md"
              data-testid="balances-adjust"
              onClick={() => setEditing(true)}
            >
              Adjust
            </Button>
          </span>
        ) : undefined
      }
    >
      <div data-testid="balances-detail-modal" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={row.employeeName} size={36} />
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            {editing
              ? "Edit each type's entitlement directly (half-day precision). Changed values are highlighted; Apply saves them with a reason."
              : 'Adjust balances here, or grant replacement in the Replacement Grants tab.'}
          </div>
        </div>

        <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="tbl" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>Leave type</th>
                <th style={{ textAlign: 'right' }}>
                  {editing ? 'Entitled (edit)' : 'Entitled'}
                </th>
                <th style={{ textAlign: 'right' }}>Used</th>
                <th style={{ textAlign: 'right' }}>Pending</th>
                <th style={{ textAlign: 'right' }}>Remaining</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {CREDIT_LEAVE_TYPES.map((lt) => {
                const cell = cells[lt as LeaveCreditsLeaveType]
                if (!cell) return null
                const style = LEAVE_TYPE_STYLE[lt]
                const isUnpaid = lt === 'unpaid'
                const delta = deltas[lt] ?? 0
                const adjustable = isAdjustable(lt)
                // Accrual-stage annual (flag on): a first-year employee's
                // remaining is measured against what they've ACCRUED so far,
                // not the full slice — so an over-draw reads as a real negative.
                // The Entitled headline still shows the full slice (e.g. 20),
                // annotated inline with "(accrued N)" so the gate is visible and
                // the negative remaining is explained. EDIT mode operates on the
                // full slice.
                const isAccrualAnnual =
                  flags.negativeAccrualBalance &&
                  lt === 'annual' &&
                  cell.accrued != null &&
                  cell.accrued < cell.entitled
                const useAccrual = isAccrualAnnual && !editing
                // Entitled headline is always the full slice (+ any edit delta).
                const entitled = cell.entitled + (adjustable ? delta : 0)
                const baseRemaining = useAccrual
                  ? (cell.accrued as number) - cell.used - cell.pending
                  : cell.remaining
                const remaining = baseRemaining + (adjustable ? delta : 0)
                const accrualNegative = useAccrual && baseRemaining < 0
                const expiry =
                  lt === 'replacement'
                    ? cell.nextExpiry
                      ? fmtDate(cell.nextExpiry)
                      : cell.remaining > 0
                        ? '—'
                        : 'none'
                    : isUnpaid
                      ? '—'
                      : cell.nextExpiry
                        ? fmtDate(cell.nextExpiry)
                        : `${CURRENT_YEAR}-12-31`
                const numStyle = {
                  textAlign: 'right' as const,
                  fontVariantNumeric: 'tabular-nums' as const,
                }
                return (
                  <tr key={lt} data-testid={`balances-detail-row-${lt}`} style={{ opacity: cell.eligible ? 1 : 0.5 }}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          aria-hidden="true"
                          style={{ width: 8, height: 8, borderRadius: '50%', background: style.swatch }}
                        />
                        {style.label}
                      </span>
                    </td>
                    {cell.eligible ? (
                      <>
                        <td
                          style={{ ...numStyle, color: delta !== 0 ? 'var(--steel-blue)' : undefined }}
                          title={
                            useAccrual
                              ? `First-year: ${fmtNum(cell.accrued as number)} of ${fmtNum(
                                  cell.entitled,
                                )} accrued so far`
                              : undefined
                          }
                        >
                          {editing && adjustable ? (
                            // Edit the entitlement directly — type the target value.
                            // Internally stored as a delta (target − current), so
                            // Apply and the backend adjustment are unchanged. A
                            // reset button appears when this type has an override,
                            // to roll it back to the cohort default.
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                justifyContent: 'flex-end',
                              }}
                            >
                              <input
                                type="number"
                                step={0.5}
                                min={0}
                                data-testid={`balances-entitled-${lt}`}
                                aria-label={`Set ${style.label} entitlement`}
                                value={entitled}
                                disabled={applying || resetting !== null}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value)
                                  setDelta(lt, (isNaN(v) ? cell.entitled : v) - cell.entitled)
                                }}
                                style={{
                                  width: 72,
                                  padding: '5px 8px',
                                  border: `1px solid ${delta !== 0 ? 'var(--steel-blue)' : 'var(--border)'}`,
                                  borderRadius: 8,
                                  background: 'var(--surface)',
                                  fontSize: 13,
                                  fontFamily: 'var(--font-mono)',
                                  fontVariantNumeric: 'tabular-nums',
                                  textAlign: 'right',
                                }}
                              />
                              {cell.overrideId ? (
                                <button
                                  type="button"
                                  data-testid={`balances-reset-${lt}`}
                                  aria-label={`Reset ${style.label} to the default — removes the override`}
                                  title="Reset to default (removes the override)"
                                  onClick={() =>
                                    setConfirmReset({
                                      lt,
                                      overrideId: cell.overrideId as string,
                                      label: style.label,
                                    })
                                  }
                                  disabled={applying || resetting !== null}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 28,
                                    height: 28,
                                    padding: 0,
                                    border: '1px solid var(--border)',
                                    borderRadius: 8,
                                    background: 'var(--surface)',
                                    color: 'var(--text-3)',
                                    cursor:
                                      applying || resetting !== null
                                        ? 'default'
                                        : 'pointer',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Icon
                                    name="restart_alt"
                                    size={16}
                                    style={{
                                      opacity: resetting === lt ? 0.5 : 1,
                                    }}
                                  />
                                </button>
                              ) : null}
                            </span>
                          ) : (
                            <>
                              {fmtNum(entitled)}
                              {useAccrual ? (
                                <span
                                  style={{
                                    color: 'var(--text-3)',
                                    fontSize: 11,
                                    fontWeight: 400,
                                    marginLeft: 4,
                                  }}
                                >
                                  (accrued {fmtNum(cell.accrued as number)})
                                </span>
                              ) : null}
                            </>
                          )}
                        </td>
                        <td style={numStyle}>{fmtNum(cell.used)}</td>
                        <td style={numStyle}>{fmtNum(cell.pending)}</td>
                        <td
                          style={{
                            ...numStyle,
                            fontWeight: 600,
                            color: delta !== 0
                              ? 'var(--steel-blue)'
                              : accrualNegative
                                ? 'var(--danger-fg)'
                                : undefined,
                          }}
                          title={
                            accrualNegative
                              ? 'Overdrawn — booked more annual leave than accrued so far'
                              : undefined
                          }
                        >
                          {isUnpaid ? '—' : fmtNum(remaining)}
                        </td>
                        <td style={{ color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{expiry}</td>
                      </>
                    ) : (
                      <td colSpan={5} style={{ color: 'var(--text-3)' }}>
                        Not applicable
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {editing ? (
          <div>
            <label
              htmlFor="balances-adjust-reason"
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}
            >
              Reason <span style={{ color: 'var(--danger-fg)' }}>*</span>
            </label>
            <textarea
              id="balances-adjust-reason"
              data-testid="balances-adjust-reason"
              ref={reasonRef}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                setError(null)
              }}
              placeholder="Required — recorded on the audit log for every changed type."
              rows={2}
              disabled={applying}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: 'var(--surface)',
                fontSize: 13,
                color: 'var(--text)',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'var(--font-sans)',
              }}
            />
            {error ? (
              <div
                role="alert"
                data-testid="balances-adjust-error"
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  background: 'var(--danger-bg)',
                  color: 'var(--danger-fg)',
                  border: '1px solid var(--danger-line)',
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            ) : null}
          </div>
        ) : null}

        <ConfirmationDialog
          open={confirmReset !== null}
          // Don't allow dismissing mid-delete.
          onClose={() => {
            if (resetting === null) setConfirmReset(null)
          }}
          onConfirm={() => {
            if (confirmReset) {
              void handleReset(confirmReset.lt, confirmReset.overrideId)
            }
          }}
          title="Remove override?"
          subtitle={
            confirmReset
              ? `This removes the ${confirmReset.label} entitlement override for ${row.employeeName} and rolls it back to the default entitlement. You can set a new override afterwards.`
              : ''
          }
          confirmLabel="Remove override"
          variant="danger"
          loading={resetting !== null}
          data-testid="balances-reset-confirm"
        />
      </div>
    </Modal>
  )
}
